import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Sparkles, Loader2, Search, Check, AlertCircle, Send, MessageSquare, Database, Download, FileSpreadsheet, FileType, FileText, Move } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { marked } from 'marked';
import { Lead, resolveStatusColor } from '../types';
import { fetchAIStatusRecommendation, getAuthHeaders } from '../lib/db';
import { DEFAULT_HALO_MODELS } from './LogisticsChatWidget';
import { exportToCSV, exportToPDF, exportToMarkdown, hasMarkdownTable } from './chatExportUtils';

interface AIAdvisorModalProps {
  isOpen: boolean;
  onClose: () => void;
  leads: Lead[];
  onUpdateStatus: (leadId: string, newStatus: string) => void | Promise<void>;
  statusColors: Record<string, string>;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  model?: string;
  toolsCalled?: string[];
}

export default function AIAdvisorModal({
  isOpen,
  onClose,
  leads,
  onUpdateStatus,
  statusColors
}: AIAdvisorModalProps) {
  const [activeTab, setActiveTab] = useState<'chat' | 'advisor'>('chat');
  
  // Tab 1: AI Chat state
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: "Hello! I am your **Armellini AI CRM Agent**, connected directly to the live database. I can query and aggregate across **Leads & Pipelines**, **Follow-Up Schedules**, **Online Forms & DocuSeal E-Signatures**, **Shippers / Consignees / Bill-To Accounts**, **Non-Floral Shipments**, **Service Quotes**, and **Tracked Gmail / Lara Emails**."
    }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [activeExportMenu, setActiveExportMenu] = useState<number | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>('gemini/gemini-4-argon');
  const [availableModels, setAvailableModels] = useState<Array<{ id: string; name: string }>>(DEFAULT_HALO_MODELS);
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 900, height: 680 });
  const isResizingRef = useRef(false);
  const resizeStartRef = useRef<{ x: number; y: number; w: number; h: number; dir: string }>({
    x: 0,
    y: 0,
    w: 900,
    h: 680,
    dir: 'se'
  });
  const chatEndRef = useRef<HTMLDivElement>(null);

  const clampModalSize = useCallback((w: number, h: number) => {
    const maxW = typeof window !== 'undefined' ? Math.min(1400, window.innerWidth - 32) : 1400;
    const maxH = typeof window !== 'undefined' ? Math.min(960, window.innerHeight - 32) : 960;
    setDimensions({
      width: Math.round(Math.max(460, Math.min(maxW, w))),
      height: Math.round(Math.max(440, Math.min(maxH, h)))
    });
  }, []);

  const onModalResize = useCallback(
    (e: MouseEvent) => {
      if (!isResizingRef.current) return;
      const { x, y, w, h, dir } = resizeStartRef.current;
      const dx = e.clientX - x;
      const dy = e.clientY - y;
      let nextW = w;
      let nextH = h;
      if (dir.includes('e')) nextW = w + dx * 2;
      if (dir.includes('w')) nextW = w - dx * 2;
      if (dir.includes('s')) nextH = h + dy * 2;
      if (dir.includes('n')) nextH = h - dy * 2;
      clampModalSize(nextW, nextH);
    },
    [clampModalSize]
  );

  const stopModalResize = useCallback(() => {
    isResizingRef.current = false;
    document.removeEventListener('mousemove', onModalResize);
    document.removeEventListener('mouseup', stopModalResize);
  }, [onModalResize]);

  const startModalResize = (dir: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isResizingRef.current = true;
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      w: dimensions.width,
      h: dimensions.height,
      dir
    };
    document.addEventListener('mousemove', onModalResize);
    document.addEventListener('mouseup', stopModalResize);
  };

  // Tab 2: Advisor state
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingLeadId, setLoadingLeadId] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<Record<string, { recommendedStatus: string; reasoning: string }>>({});
  const [appliedLeads, setAppliedLeads] = useState<Record<string, boolean>>({});

  useEffect(() => {
    async function loadConfig() {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch('/api/ai/config', { headers });
        if (res.ok) {
          const cfg = await res.json();
          if (cfg.models && Array.isArray(cfg.models) && cfg.models.length > 0) {
            setAvailableModels(cfg.models);
          }
          if (cfg.defaultModel) {
            setSelectedModel(cfg.defaultModel);
          }
        }
      } catch (e) {
        console.warn('Failed to load AI config in modal:', e);
      }
    }
    loadConfig();
  }, []);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, chatLoading]);

  const handleSendMessage = async (text: string) => {
    if (!text.trim() || chatLoading) return;
    const userMsg: Message = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: text,
          history: messages.slice(1), // omit the initial welcome message from LLM prompt history
          model: selectedModel
        })
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `Server returned status ${response.status}`);
      }

      const data = await response.json();
      const content = data.reply || data.response || "No response details returned.";
      setMessages(prev => [...prev, { role: 'assistant', content, model: data.model, toolsCalled: data.toolsCalled }]);
    } catch (err: any) {
      console.error(err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: Unable to complete query. ${err.message || 'Server error'}. Please verify connection settings and try again.`
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleGetRecommendation = async (lead: Lead) => {
    setLoadingLeadId(lead.id);
    try {
      const data = await fetchAIStatusRecommendation(lead.nextSteps || '', lead.status);
      setRecommendations(prev => ({
        ...prev,
        [lead.id]: {
          recommendedStatus: data.recommendedStatus,
          reasoning: data.reasoning
        }
      }));
    } catch (err) {
      console.error(err);
      setRecommendations(prev => ({
        ...prev,
        [lead.id]: {
          recommendedStatus: lead.status,
          reasoning: lead.nextSteps
            ? `Local Analysis: Continue following up on: "${lead.nextSteps}".`
            : "Please enter some notes in 'Next Steps' first to generate an AI suggestion."
        }
      }));
    } finally {
      setLoadingLeadId(null);
    }
  };

  const handleApplyStatus = async (leadId: string, newStatus: string) => {
    try {
      await onUpdateStatus(leadId, newStatus);
      setAppliedLeads(prev => ({ ...prev, [leadId]: true }));
      setTimeout(() => {
        setRecommendations(prev => {
          const next = { ...prev };
          delete next[leadId];
          return next;
        });
        setAppliedLeads(prev => {
          const next = { ...prev };
          delete next[leadId];
          return next;
        });
      }, 1500);
    } catch (err) {
      console.error("Failed to update status via AI Advisor:", err);
    }
  };

  const getStatusBgColor = (status: string) => {
    return resolveStatusColor(status, statusColors);
  };

  const candidates = leads.filter(lead => 
    (lead.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (lead.company || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderMarkdown = (text: string) => {
    try {
      return { __html: marked.parse(text) as string };
    } catch {
      return { __html: text };
    }
  };

  const suggestedPrompts = [
    "Which leads have overdue or upcoming follow-ups?",
    "Show pipeline summary by status and sales rep",
    "What is the status of pending online forms and DocuSeal invites?",
    "Summarize active Non-Floral shipments and closing board"
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="absolute inset-0" onClick={onClose} />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            transition={{ duration: 0.2 }}
            style={{
              width: typeof window !== 'undefined' ? Math.min(dimensions.width, window.innerWidth - 32) : dimensions.width,
              height: typeof window !== 'undefined' ? Math.min(dimensions.height, window.innerHeight - 32) : dimensions.height
            }}
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col relative overflow-hidden font-sans z-10"
          >
            {/* Corner Resize Handles */}
            <div
              onMouseDown={startModalResize('nw')}
              className="absolute top-0 left-0 w-4 h-4 cursor-nwse-resize z-50 flex items-start justify-start p-0.5 opacity-60 hover:opacity-100"
              title="Drag top-left corner to resize"
            >
              <div className="w-2.5 h-2.5 border-t-2 border-l-2 border-purple-400 rounded-tl-xs" />
            </div>
            <div
              onMouseDown={startModalResize('ne')}
              className="absolute top-0 right-0 w-4 h-4 cursor-nesw-resize z-50 flex items-start justify-end p-0.5 opacity-60 hover:opacity-100"
              title="Drag top-right corner to resize"
            >
              <div className="w-2.5 h-2.5 border-t-2 border-r-2 border-purple-400 rounded-tr-xs" />
            </div>
            <div
              onMouseDown={startModalResize('sw')}
              className="absolute bottom-0 left-0 w-4 h-4 cursor-nesw-resize z-50 flex items-end justify-start p-0.5 opacity-70 hover:opacity-100"
              title="Drag bottom-left corner to resize"
            >
              <div className="w-2.5 h-2.5 border-b-2 border-l-2 border-purple-500 rounded-bl-xs" />
            </div>
            <div
              onMouseDown={startModalResize('se')}
              className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-50 flex items-end justify-end p-0.5 opacity-70 hover:opacity-100"
              title="Drag bottom-right corner to resize"
            >
              <div className="w-2.5 h-2.5 border-b-2 border-r-2 border-purple-500 rounded-br-xs" />
            </div>
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <img
                  src="/chatbot-icon.jpg"
                  alt="Armellini AI Chatbot"
                  className="w-10 h-10 rounded-full object-cover border-2 border-purple-500 shadow-sm shrink-0"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-black text-slate-800 dark:text-slate-100">Armellini AI Hub</h2>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-950/70 text-purple-700 dark:text-purple-300 font-mono border border-purple-200 dark:border-purple-800">
                      Halo AI
                    </span>
                  </div>
                  {availableModels.length > 0 ? (
                    <select
                      value={selectedModel}
                      onChange={e => setSelectedModel(e.target.value)}
                      className="text-[10px] bg-slate-100 dark:bg-slate-800 text-purple-600 dark:text-purple-300 font-medium rounded px-1.5 py-0.5 border border-slate-200 dark:border-slate-750 outline-none hover:border-purple-400 cursor-pointer mt-0.5 max-w-[240px] truncate"
                      title="Active LLM Model"
                    >
                      {availableModels.map(m => (
                        <option key={m.id} value={m.id} className="bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100">
                          {m.name || m.id}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">Connected database agent search ({selectedModel})</p>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Tabs Selector */}
            <div className="flex border-b border-slate-150 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-900/20 px-6">
              <button
                onClick={() => setActiveTab('chat')}
                className={`py-3 px-4 text-xs font-black cursor-pointer border-b-2 transition flex items-center gap-1.5 ${
                  activeTab === 'chat'
                    ? 'border-purple-600 text-purple-600 dark:text-purple-400 font-extrabold'
                    : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                AI CRM Copilot
              </button>
              <button
                onClick={() => setActiveTab('advisor')}
                className={`py-3 px-4 text-xs font-black cursor-pointer border-b-2 transition flex items-center gap-1.5 ${
                  activeTab === 'advisor'
                    ? 'border-purple-600 text-purple-600 dark:text-purple-400 font-extrabold'
                    : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Database className="w-3.5 h-3.5" />
                Pipeline Advisor
              </button>
            </div>

            {/* Tab 1: AI CRM Copilot */}
            {activeTab === 'chat' && (
              <div className="flex-1 flex flex-col min-h-0 bg-slate-50/30 dark:bg-slate-950/20">
                {/* Chat Message Window */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`${
                          msg.role === 'assistant' && hasMarkdownTable(msg.content) ? 'w-full max-w-[96%]' : 'max-w-[85%]'
                        } rounded-2xl px-4 py-3 text-xs shadow-sm leading-relaxed ${
                          msg.role === 'user'
                            ? 'bg-purple-600 text-white rounded-tr-none'
                            : 'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-850 rounded-tl-none'
                        }`}
                      >
                        {msg.toolsCalled && msg.toolsCalled.length > 0 && (
                          <div className="mb-2 inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 dark:bg-purple-950/60 border border-purple-200 dark:border-purple-800 rounded text-[10px] font-mono text-purple-700 dark:text-purple-300">
                            <Database className="w-3 h-3" />
                            <span>Queried: {msg.toolsCalled.join(', ')}</span>
                          </div>
                        )}
                        {msg.role === 'assistant' ? (
                          <div
                            className="prose prose-xs dark:prose-invert max-w-none overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_th]:bg-slate-100 dark:[&_th]:bg-slate-800 [&_th]:p-2 [&_td]:p-2 [&_th]:border [&_td]:border [&_th]:border-slate-300 dark:[&_th]:border-slate-700 [&_td]:border-slate-300 dark:[&_td]:border-slate-700 [&_table]:text-xs"
                            dangerouslySetInnerHTML={renderMarkdown(msg.content)}
                          />
                        ) : (
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                        )}
                        {msg.role === 'assistant' && (
                          <div className="mt-3 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2 flex-wrap text-xs text-slate-500">
                            <div className="flex items-center gap-2">
                              {msg.model && (
                                <span className="text-[9px] px-1.5 py-0.2 rounded bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-300 font-mono">
                                  ⚡ {msg.model}
                                </span>
                              )}
                            </div>

                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => setActiveExportMenu(activeExportMenu === index ? null : index)}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-purple-50 dark:hover:bg-purple-950/50 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:text-purple-600 dark:hover:text-purple-400 font-semibold cursor-pointer transition"
                                title="Export result as Markdown, PDF, or CSV"
                              >
                                <Download className="w-3.5 h-3.5" />
                                <span>Export</span>
                              </button>

                              {activeExportMenu === index && (
                                <div className="absolute right-0 bottom-9 z-40 w-44 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl py-1.5 text-xs">
                                  <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 dark:border-slate-800 mb-1">
                                    Choose Format
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      exportToCSV(msg.content);
                                      setActiveExportMenu(null);
                                    }}
                                    className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer font-medium text-slate-700 dark:text-slate-200"
                                  >
                                    <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                    <span>CSV {hasMarkdownTable(msg.content) ? '(Table)' : '(Data)'}</span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      exportToPDF(msg.content);
                                      setActiveExportMenu(null);
                                    }}
                                    className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer font-medium text-slate-700 dark:text-slate-200"
                                  >
                                    <FileType className="w-3.5 h-3.5 text-red-500 shrink-0" />
                                    <span>PDF (Report)</span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      exportToMarkdown(msg.content);
                                      setActiveExportMenu(null);
                                    }}
                                    className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer font-medium text-slate-700 dark:text-slate-200"
                                  >
                                    <FileText className="w-3.5 h-3.5 text-sky-500 shrink-0" />
                                    <span>Markdown (.md)</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 text-purple-600 animate-spin" />
                        <span className="text-xs text-slate-500 font-medium animate-pulse">AI Agent searching database...</span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Suggested Prompts */}
                {messages.length === 1 && (
                  <div className="px-6 py-2 flex flex-wrap gap-2 justify-center">
                    {suggestedPrompts.map((p, i) => (
                      <button
                        key={i}
                        onClick={() => handleSendMessage(p)}
                        className="text-[10px] bg-slate-100 hover:bg-purple-50 dark:bg-slate-800 dark:hover:bg-purple-950/20 text-slate-600 hover:text-purple-600 dark:text-slate-350 dark:hover:text-purple-400 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700/50 cursor-pointer transition font-medium"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}

                {/* Input Bar */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSendMessage(chatInput);
                  }}
                  className="p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex gap-2.5 items-center"
                >
                  <input
                    type="text"
                    placeholder="Search database, clients, shippers or email summaries..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    className="flex-1 px-4 py-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-slate-800 dark:text-slate-200 font-medium"
                  />
                  <button
                    type="submit"
                    disabled={!chatInput.trim() || chatLoading}
                    className={`p-2.5 rounded-xl text-white shadow-sm flex items-center justify-center transition cursor-pointer ${
                      !chatInput.trim() || chatLoading
                        ? 'bg-slate-300 dark:bg-slate-800 cursor-not-allowed text-slate-400'
                        : 'bg-purple-600 hover:bg-purple-700'
                    }`}
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              </div>
            )}

            {/* Tab 2: Pipeline Advisor */}
            {activeTab === 'advisor' && (
              <>
                {/* Search filter */}
                <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-850 flex items-center gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search leads by name or company..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-850 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-slate-800 dark:text-slate-200"
                    />
                  </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {candidates.length > 0 ? (
                    candidates.map((lead) => {
                      const recommendation = recommendations[lead.id];
                      const isLoading = loadingLeadId === lead.id;
                      const isApplied = appliedLeads[lead.id];

                      return (
                        <div
                          key={lead.id}
                          className="border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 bg-white dark:bg-slate-950 flex flex-col md:flex-row justify-between gap-4 transition hover:shadow-md"
                        >
                          {/* Left: Lead Details */}
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2.5 flex-wrap">
                              <span className="font-bold text-slate-800 dark:text-slate-100 text-sm">{lead.name}</span>
                              <span className="text-xs text-slate-400 font-medium">({lead.company})</span>
                              <span
                                className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded font-mono"
                                style={{
                                  backgroundColor: `${getStatusBgColor(lead.status)}20`,
                                  color: getStatusBgColor(lead.status),
                                  border: `1px solid ${getStatusBgColor(lead.status)}40`
                                }}
                              >
                                {lead.status}
                              </span>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-900/60 rounded-lg p-2.5 text-xs text-slate-600 dark:text-slate-350 border border-slate-100 dark:border-slate-850">
                              <span className="font-semibold text-slate-400 block mb-0.5 text-[10px] uppercase tracking-wide">Next Steps / Activity Log</span>
                              <p className="italic">{lead.nextSteps || 'No next steps populated.'}</p>
                            </div>
                          </div>

                          {/* Right: Action */}
                          <div className="w-full md:w-80 flex flex-col justify-center items-end gap-3 self-stretch border-t md:border-t-0 md:border-l border-slate-100 dark:border-slate-850 pt-3 md:pt-0 md:pl-4">
                            {!recommendation ? (
                              <button
                                onClick={() => handleGetRecommendation(lead)}
                                disabled={isLoading || !lead.nextSteps}
                                className={`w-full py-2 px-3 text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 cursor-pointer transition ${
                                  isLoading
                                    ? 'bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 animate-pulse'
                                    : !lead.nextSteps
                                    ? 'bg-slate-100 dark:bg-slate-800/50 text-slate-400 cursor-not-allowed'
                                    : 'bg-purple-600 hover:bg-purple-700 text-white shadow-sm'
                                }`}
                              >
                                {isLoading ? (
                                  <>
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    <span>Analyzing Lead...</span>
                                  </>
                                ) : (
                                  <>
                                    <Sparkles className="w-3.5 h-3.5" />
                                    <span>Get AI Advice</span>
                                  </>
                                )}
                              </button>
                            ) : (
                              <div className="w-full space-y-2.5 text-left">
                                <div className="flex items-start gap-1.5 text-xs text-slate-600 dark:text-slate-350">
                                  <AlertCircle className="w-3.5 h-3.5 text-purple-500 shrink-0 mt-0.5" />
                                  <p className="leading-tight"><span className="font-bold text-slate-700 dark:text-slate-200">Advice:</span> {recommendation.reasoning}</p>
                                </div>

                                <div className="flex items-center gap-2">
                                  {recommendation.recommendedStatus === lead.status ? (
                                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-50 dark:bg-emerald-950/30 px-2 py-1 rounded w-full text-center border border-emerald-200/50">
                                      ✓ Current status is optimal
                                    </span>
                                  ) : (
                                    <button
                                      onClick={() => handleApplyStatus(lead.id, recommendation.recommendedStatus)}
                                      disabled={isApplied}
                                      className={`w-full py-2 px-3 text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 cursor-pointer transition ${
                                        isApplied
                                          ? 'bg-emerald-600 text-white'
                                          : 'bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 text-emerald-700 dark:text-emerald-355 border border-emerald-250'
                                      }`}
                                    >
                                      {isApplied ? (
                                        <>
                                          <Check className="w-3.5 h-3.5" />
                                          <span>Applied!</span>
                                        </>
                                      ) : (
                                        <>
                                          <span>Change to </span>
                                          <span
                                            className="px-1.5 py-0.5 rounded text-[10px] font-extrabold uppercase ml-1"
                                            style={{
                                              backgroundColor: getStatusBgColor(recommendation.recommendedStatus),
                                              color: '#fff'
                                            }}
                                          >
                                            {recommendation.recommendedStatus === 'Revisit Later | ADD FU DATE' ? 'Revisit Later' : recommendation.recommendedStatus}
                                          </span>
                                        </>
                                      )}
                                    </button>
                                  )}

                                  <button
                                    onClick={() => {
                                      setRecommendations(prev => {
                                        const next = { ...prev };
                                        delete next[lead.id];
                                        return next;
                                      });
                                    }}
                                    className="p-2 border border-slate-250 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg text-slate-400 hover:text-slate-600 transition"
                                    title="Clear suggestion"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="py-12 text-center text-slate-400 dark:text-slate-500">
                      No matching leads found.
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Bottom Resize & Window Size Control Bar */}
            <div className="px-4 py-1.5 bg-slate-100 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400 select-none shrink-0">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => clampModalSize(dimensions.width - 100, dimensions.height - 60)}
                  className="px-2 py-0.5 rounded bg-white dark:bg-slate-800 hover:bg-purple-50 dark:hover:bg-purple-950/50 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold cursor-pointer transition"
                  title="Make chat window smaller"
                >
                  − Smaller
                </button>
                <button
                  type="button"
                  onClick={() => clampModalSize(900, 680)}
                  className="px-2 py-0.5 rounded bg-white dark:bg-slate-800 hover:bg-purple-50 dark:hover:bg-purple-950/50 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-mono cursor-pointer transition"
                  title="Reset to default 900px width"
                >
                  900px Default
                </button>
                <button
                  type="button"
                  onClick={() => clampModalSize(dimensions.width + 100, dimensions.height + 60)}
                  className="px-2 py-0.5 rounded bg-white dark:bg-slate-800 hover:bg-purple-50 dark:hover:bg-purple-950/50 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold cursor-pointer transition"
                  title="Make chat window larger"
                >
                  + Larger
                </button>
              </div>

              <div
                onMouseDown={startModalResize('s')}
                className="flex items-center gap-1.5 px-3 py-1 cursor-ns-resize hover:text-purple-500 transition-colors"
                title="Drag bottom edge to resize height"
              >
                <div className="w-8 h-1 rounded-full bg-slate-300 dark:bg-slate-700" />
                <span className="font-mono text-[9px] hidden sm:inline">
                  {dimensions.width}×{dimensions.height}px
                </span>
              </div>

              <div
                onMouseDown={startModalResize('se')}
                className="flex items-center gap-1 px-1.5 py-0.5 cursor-nwse-resize hover:text-purple-500 transition-colors"
                title="Drag corner to resize window"
              >
                <Move className="w-3 h-3" />
                <span className="text-[9px] hidden md:inline">Resize Corner</span>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
